import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Fish,
  Crown,
  Gauge,
  TelegramLogo,
  Sliders,
  ListBullets,
  Password,
  Bell,
  UsersThree,
  SignOut,
  List,
  X,
  PencilSimple,
  Trash,
  ArrowsLeftRight,
  CaretDown,
  Check,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth.jsx";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const NAV = [
  { to: "/dashboard", label: "Status", icon: Gauge, testid: "nav-status" },
  { to: "/dashboard/telegram", label: "Telegram", icon: TelegramLogo, testid: "nav-telegram" },
  { to: "/dashboard/config", label: "Konfigurasi", icon: Sliders, testid: "nav-config" },
  { to: "/dashboard/activity", label: "Activity Log", icon: ListBullets, testid: "nav-activity" },
  { to: "/dashboard/notifications", label: "Notifikasi", icon: Bell, testid: "nav-notifications" },
  { to: "/dashboard/pricing", label: "Langganan", icon: Crown, testid: "nav-subscription" },
];

const PAGE_TITLE = {
  "/dashboard": "Status bot", "/dashboard/status": "Status bot",
  "/dashboard/telegram": "Akun Telegram", "/dashboard/config": "Konfigurasi",
  "/dashboard/activity": "Aktivitas", "/dashboard/notifications": "Notifikasi",
  "/dashboard/otp": "Kode OTP",
  "/dashboard/pricing": "Langganan", "/dashboard/admin": "Kelola pelanggan",
  "/dashboard/coin-transfer": "Pindah koin",
  "/dashboard/auto-listing": "Auto Listing",
};

const PLAN_UI = {
  free: { limit: 1, plan_label: "Starter" },
  basic: { limit: 1, plan_label: "Starter" },
  pro: { limit: 1, plan_label: "Pro" },
  elite: { limit: 3, plan_label: "Elite" },
  admin: { limit: 100, plan_label: "Admin" },
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [newAccountLabel, setNewAccountLabel] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [accInfo, setAccInfo] = useState(
    () => PLAN_UI[(user?.plan || "free").toLowerCase()] || PLAN_UI.free
  );
  const [activeAcc, setActiveAcc] = useState(localStorage.getItem("fishit_account") || "");
  const location = useLocation();
  const activeAccount = accounts.find((account) => account.id === activeAcc);
  const mainNav = NAV.map((item) => item.to === "/dashboard/activity" && user?.role !== "admin"
    ? { to: "/dashboard/otp", label: "Kode OTP", icon: Password, testid: "nav-otp" }
    : item).concat(user?.role === "admin" || user?.plan === "elite"
      ? [{ to: "/dashboard/auto-listing", label: "Auto Listing", icon: Fish, testid: "nav-auto-listing" }]
      : []);

  useEffect(() => {
    setOpen(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    api.get("/health")
      .then((response) => {
        if (active) setRecoveryMode(Boolean(response.data?.recovery_mode));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) setAccountMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [accountMenuOpen]);

  useEffect(() => {
    api
      .get("/telegram/accounts")
      .then((r) => {
        const list = r.data.accounts || [];
        setAccounts(list);
        setAccInfo({ limit: r.data.limit, plan_label: r.data.plan_label });
        let sel = localStorage.getItem("fishit_account");
        const selectedIndex = list.findIndex((a) => a.id === sel);
        if (list[0] && sel && selectedIndex >= r.data.limit) {
          localStorage.setItem('fishit_account',list[0].id);
          window.location.reload();
          return;
        }
        if (!sel || !list.find((a) => a.id === sel)) {
          sel = list[0]?.id || "";
          if (sel) localStorage.setItem("fishit_account", sel);
        }
        setActiveAcc(sel);
      })
      .catch(() => {});
  }, [user?.plan]);

  const accountSwitchPendingRef = useRef(false);
  const switchAcc = async (id) => {
    if (!id || id === activeAcc) return;
    if (accountSwitchPendingRef.current) return;
    accountSwitchPendingRef.current = true;
    try {
      const saves = [];
      window.dispatchEvent(new CustomEvent("fishit-before-account-switch", {
        detail: { waitUntil: (promise) => saves.push(Promise.resolve(promise)) },
      }));
      if ((await Promise.all(saves)).some((saved) => saved === false)) {
        toast.warning("Pengaturan belum tersimpan. Tunggu proses bot selesai atau perbaiki nilai sebelum mengganti akun.");
        return;
      }
      localStorage.setItem("fishit_account", id);
      window.location.reload();
    } finally {
      accountSwitchPendingRef.current = false;
    }
  };

  const openAddAccount = () => {
    setNewAccountLabel(`Akun ${accounts.length + 1}`);
    setAddAccountOpen(true);
  };

  const addAcc = async (event) => {
    event.preventDefault();
    const label = newAccountLabel.trim() || `Akun ${accounts.length + 1}`;
    setAddingAccount(true);
    try {
      const r = await api.post("/telegram/accounts", { label });
      localStorage.setItem("fishit_account", r.data.id);
      toast.success("Akun Telegram baru dibuat");
      setAddAccountOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tidak bisa menambah akun");
    } finally {
      setAddingAccount(false);
    }
  };

  const renameAcc = async () => {
    const current = accounts.find((a) => a.id === activeAcc);
    if (!current) return;
    const label = window.prompt("Ubah label akun (nama pelanggan):", current.label);
    if (label === null || !label.trim()) return;
    try {
      await api.patch(`/telegram/accounts/${activeAcc}`, { label: label.trim() });
      setAccounts((prev) => prev.map((a) => (a.id === activeAcc ? { ...a, label: label.trim() } : a)));
      toast.success("Label akun diperbarui");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah label");
    }
  };

  const deleteAcc = async () => {
    const current = accounts.find((a) => a.id === activeAcc);
    if (!current) return;
    if (accounts.length <= 1) {
      toast.error("Minimal harus ada 1 akun");
      return;
    }
    if (!window.confirm(`Hapus akun "${current.label}"? Session Telegram-nya akan dilogout & data akun ini dihapus.`)) return;
    try {
      await api.delete(`/telegram/accounts/${activeAcc}`);
      localStorage.removeItem("fishit_account");
      toast.success("Akun dihapus & session Telegram dilogout");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus akun");
    }
  };

  useEffect(() => {
    const load = () => {
      api
        .get("/automation/notifications", { params: { limit: 1 } })
        .then((r) => setUnread(r.data.unread_count || 0))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [location.pathname]);

  return (
    <div className="dashboard-shell min-h-screen text-slate-100 flex">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-[60] h-16 dashboard-mobile-header flex items-center justify-between px-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 shrink-0 rounded-lg bg-pink-500 flex items-center justify-center">
            <Fish size={19} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-[13px] tracking-tight">FISH IT</div>
            <div className="text-[11px] text-slate-400 truncate">{activeAccount?.label || "Autopilot"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NavLink to="/dashboard/notifications" aria-label="Notifikasi" className="relative flex h-11 w-11 items-center justify-center rounded-xl text-slate-300 hover:bg-white/10">
            <Bell size={21} />
            {unread > 0 && <span className="absolute right-1 top-1 min-w-4 rounded-full bg-pink-400 px-1 text-center text-[10px] font-bold text-slate-950">{unread}</span>}
          </NavLink>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-200 hover:bg-white/10 transition-colors"
            aria-label={open ? "Tutup menu" : "Buka menu"}
            aria-expanded={open}
          >
            {open ? <X size={23} /> : <List size={23} />}
          </button>
        </div>
      </div>

      {/* Overlay (mobile) */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          data-testid="mobile-overlay"
          className="md:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`dashboard-sidebar w-[min(20rem,88vw)] md:w-[17.5rem] shrink-0 flex flex-col fixed md:sticky md:top-0 md:h-screen inset-y-0 left-0 z-50 transform transition-transform duration-300 md:translate-x-0 overflow-y-auto ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-6 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-pink-500 flex items-center justify-center">
              <Fish size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm tracking-tight">FISH IT</div>
              <div className="text-[11px] tracking-wide text-slate-400">Panel kontrol</div>
            </div>
          </div>
        </div>
        {/* Account switcher (multi-account per plan) */}
        <div className="m-3 p-3 rounded-2xl dashboard-account-panel" data-testid="account-switcher-box">
          <div className="text-[11px] font-semibold tracking-wide text-slate-400 mb-2 flex items-center justify-between">
            <span>Akun Telegram</span>
            <span className="text-slate-400 normal-case tracking-normal font-mono">
              {accounts.length}/{accInfo.limit}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div ref={accountMenuRef} className="relative flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setAccountMenuOpen((value) => !value)}
                data-testid="account-switcher"
                aria-haspopup="listbox"
                aria-expanded={accountMenuOpen}
                className="w-full min-w-0 h-11 bg-[#0d1420] border border-white/10 rounded-xl text-sm px-3 text-slate-100 focus-visible:ring-2 focus-visible:ring-pink-400 outline-none flex items-center justify-between gap-2"
              >
                <span className="truncate flex items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${activeAccount?.connected ? "bg-emerald-400" : "bg-slate-500"}`} />
                  <span className="truncate">{activeAccount?.label || "Pilih akun"}</span>
                </span>
                <CaretDown
                  size={14}
                  className={`shrink-0 text-slate-500 transition-transform ${accountMenuOpen ? "rotate-180" : ""}`}
                />
              </button>
              {accountMenuOpen && (
                <div
                  role="listbox"
                  aria-label="Akun Telegram"
                  className="absolute z-[80] left-0 right-0 mt-2 max-h-64 overflow-y-auto overscroll-contain rounded-xl border border-white/15 bg-[#131d2b] p-1.5 shadow-2xl scrollbar-thin"
                >
                  {accounts.map((account, index) => {
                    const disabled = index >= accInfo.limit;
                    const selected = account.id === activeAcc;
                    return (
                      <button
                        key={account.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        disabled={disabled}
                        onClick={() => {
                          setAccountMenuOpen(false);
                          switchAcc(account.id);
                        }}
                        className={`w-full min-h-11 px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2 ${
                          selected
                            ? "bg-pink-500/15 text-pink-300"
                            : "text-slate-300 hover:bg-white/5"
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {account.label}
                          {disabled ? ` · nonaktif (batas ${accInfo.limit} akun)` : ""}
                          {account.connected ? " · ✓" : " · —"}
                        </span>
                        {selected && <Check size={14} weight="bold" className="shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={renameAcc}
              data-testid="account-rename"
              title="Ubah label / nama pelanggan"
              aria-label="Ubah nama akun"
              className="shrink-0 h-11 w-9 flex items-center justify-center rounded-xl border border-white/10 text-slate-400 hover:text-pink-300 hover:border-pink-500/40 transition-colors"
            >
              <PencilSimple size={14} />
            </button>
            <button
              onClick={deleteAcc}
              data-testid="account-delete"
              title="Hapus akun & logout session Telegram"
              aria-label="Hapus akun"
              className="shrink-0 h-11 w-9 flex items-center justify-center rounded-xl border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-500/40 transition-colors"
            >
              <Trash size={14} />
            </button>
          </div>
          <button
            onClick={openAddAccount}
            disabled={accounts.length >= accInfo.limit}
            data-testid="account-add"
            className="mt-2 w-full min-h-10 text-xs font-semibold px-2 py-2 rounded-xl border border-pink-500/30 text-pink-300 hover:bg-pink-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={accounts.length >= accInfo.limit ? `Paket ${accInfo.plan_label} maksimal ${accInfo.limit} akun` : ""}
          >
            + Tambah akun ({accInfo.plan_label})
          </button>
        </div>
        <nav className="flex-1 px-3 py-3 space-y-1" aria-label="Navigasi utama">
          <div className="px-3 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Menu utama</div>
          {mainNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/dashboard"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `dashboard-nav-link flex items-center gap-3 px-3 min-h-11 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "is-active bg-pink-500/10 text-pink-200 border border-pink-500/20"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span>{n.label}</span>
              {n.to === "/dashboard/notifications" && unread > 0 && (
                <span className="ml-auto bg-pink-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="nav-unread-badge">
                  {unread}
                </span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 px-3 pt-6 pb-2">
                Pengelolaan
              </div>
              <NavLink
                to="/dashboard/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `dashboard-nav-link flex items-center gap-3 px-3 min-h-11 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "is-active bg-amber-500/10 text-amber-200 border border-amber-500/20"
                      : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                  }`
                }
              >
                <UsersThree size={18} weight="duotone" />
                <span>Pelanggan</span>
              </NavLink>
              {user?.email?.toLowerCase() === "admin@fishit.app" && (
                <NavLink
                  to="/dashboard/coin-transfer"
                  data-testid="nav-coin-transfer"
                  className={({ isActive }) =>
                    `dashboard-nav-link flex items-center gap-3 px-3 min-h-11 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? "is-active bg-amber-500/10 text-amber-200 border border-amber-500/20"
                        : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                    }`
                  }
                >
                  <ArrowsLeftRight size={18} weight="duotone" />
                  <span>Pindah Koin</span>
                </NavLink>
              )}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-white/[0.07]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-pink-500/20 border border-pink-500/40 flex items-center justify-center text-pink-400 text-xs font-bold">
              {user?.email?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate" data-testid="sidebar-user-email">{user?.email}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                {user?.role} · {user?.plan}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout"
            className="w-full min-h-11 flex items-center gap-2 text-sm text-slate-400 hover:text-white px-3 py-2 rounded-xl hover:bg-white/5 transition-colors"
          >
            <SignOut size={16} />
            <span>Keluar</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="dashboard-main flex-1 min-w-0 pt-16 md:pt-0 pb-24 md:pb-0">
        <div className="dashboard-pagebar hidden md:flex items-center justify-between gap-4 px-8 py-4">
          <div>
            <div className="text-xs text-slate-400">Panel kontrol</div>
            <div className="text-base font-semibold text-slate-100">{PAGE_TITLE[location.pathname] || "Fish It"}</div>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
            <span className={`h-2 w-2 rounded-full ${activeAccount?.connected ? "bg-emerald-400" : "bg-slate-500"}`} />
            <span className="text-slate-300">{activeAccount?.label || "Pilih akun Telegram"}</span>
          </div>
        </div>
        <div className="px-4 py-6 sm:px-6 md:p-8 max-w-7xl mx-auto">
          {recoveryMode && (
            <div role="status" className="mb-5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
              Pemulihan VPS sedang berlangsung. Akun Telegram yang sudah tersambung ulang dapat berjalan; beberapa jadwal lain sementara dijeda. Jika Telegram terlihat terputus, sambungkan ulang akun Anda. Data dan pengaturan tetap tersimpan.
            </div>
          )}
          <Outlet />
        </div>
      </main>

      <nav className="dashboard-mobile-nav md:hidden fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 px-2 pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-1" aria-label="Navigasi cepat">
        {[mainNav[0], mainNav[2], mainNav[3], mainNav[4]].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/dashboard"} className={({ isActive }) => `flex min-h-[54px] flex-col items-center justify-center gap-0.5 rounded-xl text-[10px] font-semibold ${isActive ? "text-pink-300 bg-pink-500/10" : "text-slate-400"}`}>
            <n.icon size={21} weight="duotone" />
            <span>{n.label === "Activity Log" ? "Aktivitas" : n.label}</span>
          </NavLink>
        ))}
        <button type="button" onClick={() => setOpen(true)} className="flex min-h-[54px] flex-col items-center justify-center gap-0.5 rounded-xl text-[10px] font-semibold text-slate-400" aria-label="Buka semua menu">
          <List size={21} />
          <span>Lainnya</span>
        </button>
      </nav>

      <Dialog open={addAccountOpen} onOpenChange={(value) => !addingAccount && setAddAccountOpen(value)}>
        <DialogContent className="max-w-md border-white/10 bg-[#0F0F16] text-slate-100">
          <form onSubmit={addAcc} className="space-y-5">
            <DialogHeader>
              <DialogTitle>Tambah akun Telegram</DialogTitle>
              <DialogDescription className="text-slate-400">
                Buat slot akun baru. Setelah dibuat, halaman akan berpindah ke akun tersebut untuk proses login Telegram.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label htmlFor="new-telegram-account-label" className="text-sm font-medium text-slate-200">Nama akun</label>
              <Input
                id="new-telegram-account-label"
                value={newAccountLabel}
                onChange={(event) => setNewAccountLabel(event.target.value)}
                placeholder="Contoh: Budi"
                autoFocus
                maxLength={80}
                className="bg-[#05050A] border-white/15"
              />
            </div>
            <DialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" variant="outline" disabled={addingAccount} onClick={() => setAddAccountOpen(false)}>Batal</Button>
              <Button type="submit" disabled={addingAccount} className="bg-pink-500 text-black hover:bg-pink-400">
                {addingAccount ? "Membuat..." : "Tambah akun"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
